import { useToast } from '@chakra-ui/react';
import { QueryKey, useQuery } from '@tanstack/react-query';
import { AxiosInstance } from 'axios';
import { useAxios } from 'context/AxiosContextProvider';
import { manageErrorResponse } from 'helpers/manageErrorResponse';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { User } from '../api/types';
import config from '../config';

interface OPUserParams {
  axios: AxiosInstance;
  id: number;
}

async function getOPUser({ axios, id }: OPUserParams): Promise<User> {
  const { data } = await axios.get(`${config.apiBaseUrl}/op/v1/users/${id}`);

  return data;
}

export default function useOPUserQuery(id: number | null) {
  const axios = useAxios();
  const toast = useToast();
  const navigate = useNavigate();

  const query = useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: getKey(id),
    queryFn: () => getOPUser({ axios, id: id! }),
    enabled: id != null,
    notifyOnChangeProps: ['data', 'isFetching', 'error'],
    retry: false,
    staleTime: 0,
  });

  useEffect(() => {
    if (query.isError) {
      const error = query.error as any;
      if (error?.response?.status === 403) {
        return
      }
      if (error?.response?.status !== 503) {
        manageErrorResponse(toast, error);
      }
      if (error?.response?.status >= 500 && error?.response?.status !== 503) {
        navigate('/app/errorPage', { replace: true });
      }
    }
  }, [navigate, query.error, query.isError, toast]);

  return query;
}

function getKey(id: number | null) {
  return ['OPUser', id] as const satisfies QueryKey;
}
useOPUserQuery.getKey = getKey;
